import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { Box } from '@mantine/core';
import { Link } from 'react-router-dom';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getWorkOrderListQuery } from '../../../slices/workOrder/thunk';
import { setRequireRefetch } from '../../../slices/workOrder/reducer';
import AddWorkModal from '../Work/Modals/AddWorkModal';
import DeleteWorkModal from '../Work/Modals/DeleteWorkModal';
import WorkFileModal from '../Work/Modals/WorkFileModal';

const WorkDetailsPanel = ({ waterRightData }) => {
	const dispatch = useDispatch();
	const [modalWork, setModalWork] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [modalFile, setModalFile] = useState(false);
	const [selectedWork, setSelectedWork] = useState(null);
	const [refetch, setRefetch] = useState(0);

	const selectorWorkOrder = createSelector(
		state => state.WorkOrder,
		data => ({
			requireRefetch: data.requireRefetch,
		})
	);

	const { requireRefetch } = useSelector(selectorWorkOrder);

	useEffect(() => {
		if (requireRefetch) {
			setRefetch(prev => prev + 1);
			dispatch(setRequireRefetch(false));
		}
	}, [requireRefetch, dispatch]);

	const handleToggleWorkModal = () => {
		setModalWork(!modalWork);
		if (modalWork) setSelectedWork(null);
	};

	const handleToggleDeleteModal = () => {
		setModalDelete(!modalDelete);
		if (modalDelete) setSelectedWork(null);
	};

	const handleToggleFileModal = () => {
		setModalFile(!modalFile);
		if (modalFile) setSelectedWork(null);
	};

	const handleClickAdd = () => {
		setSelectedWork(null);
		setModalWork(true);
	};

	const handleClickEdit = useCallback(workData => {
		setSelectedWork(workData);
		setModalWork(true);
	}, []);

	const handleClickDelete = useCallback(workData => {
		setSelectedWork(workData);
		setModalDelete(true);
	}, []);

	const handleClickFile = useCallback(workData => {
		setSelectedWork(workData);
		setModalFile(true);
	}, []);

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: ({ row }) => {
					const workData = row.original;
					return (
						<div className='d-flex justify-content-center'>
							<div className='flex-shrink-0'>
								<ul className='list-inline mb-0'>
									<li className='list-inline-item'>
										<Link
											title='Editar'
											to='#'
											onClick={() => handleClickEdit(workData)}
										>
											<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
										</Link>
									</li>
									{workData.file_id && (
										<li className='list-inline-item'>
											<Link
												title='Ver Documento'
												to='#'
												onClick={() => handleClickFile(workData)}
											>
												<i className='ri-file-search-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									)}
									<li className='list-inline-item'>
										<Link
											title='Ver Detalle'
											to={`/water-rights/work/${workData._id}`}
										>
											<i className='ri-search-line align-bottom me-2 text-muted'></i>
										</Link>
									</li>
									<li className='list-inline-item'>
										<Link
											title='Eliminar'
											to='#'
											className='remove-item-btn'
											onClick={() => handleClickDelete(workData)}
										>
											<i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
										</Link>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
			{
				header: 'Código de Obra',
				dataType: 'string',
				accessorKey: 'work_order_code',
			},
			{
				header: 'Estado de Obra',
				dataType: 'select',
				accessorKey: 'work_order_status.name',
			},
			{
				header: 'Tipo de Reportabilidad',
				dataType: 'select',
				accessorKey: 'reporting_type.name',
			},
			{
				header: 'Frecuencia de Reportabilidad',
				dataType: 'select',
				accessorKey: 'reporting_frequency.name',
			},
			{
				header: 'Frecuencia de Mediciones',
				dataType: 'select',
				accessorKey: 'measurement_frequency.name',
			},
			{
				header: 'Posición',
				dataType: 'select',
				accessorKey: 'position.name',
			},
			{
				header: 'Fecha de Inicio de Reportabilidad',
				dataType: 'date',
				accessorKey: 'reporting_start_date',
				Cell: ({ row }) => {
					const date = row.original.reporting_start_date;
					if (!date) return '-';
					return (
						<span>
							{new Date(date).toLocaleDateString('es-ES', {
								day: '2-digit',
								month: '2-digit',
								year: 'numeric',
							})}
						</span>
					);
				},
			},
		],
		[handleClickEdit, handleClickDelete, handleClickFile]
	);

	return (
		<>
			<Box
				className='p-2'
				sx={{
					display: 'Grid',
					margin: 'auto',
					width: '100%',
				}}
			>
				<ODataGrid
					columns={columns}
					getDataFunc={getWorkOrderListQuery}
					baseFilters={{
						water_right_id: { $eq: waterRightData?._id },
						disable: { $eq: false },
					}}
					remoteOperations={{
						pagination: true,
						sorting: true,
						filtering: true,
					}}
					queryPopulate={[
						'work_order_status',
						'reporting_type',
						'reporting_frequency',
						'measurement_frequency',
						'position',
					]}
					refetchRemoteData={refetch}
					nameFile='obras'
				/>
			</Box>

			{modalWork && (
				<AddWorkModal
					waterRightData={waterRightData}
					dataToEdit={selectedWork}
					isOpen={modalWork}
					handleToggleModal={handleToggleWorkModal}
				/>
			)}

			{modalDelete && selectedWork && (
				<DeleteWorkModal
					dataWork={selectedWork}
					isOpen={modalDelete}
					handlerToggleModal={handleToggleDeleteModal}
				/>
			)}

			{modalFile && selectedWork && (
				<WorkFileModal
					workData={selectedWork}
					isOpen={modalFile}
					handleToggleModal={handleToggleFileModal}
				/>
			)}
		</>
	);
};

export default WorkDetailsPanel;

